import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface KlingV2MasterI2vParams {
  /**
   * 输入图像URL或Base64编码，与image_tail二选一（至少提供其中一个）
   */
  image?: string;
  /**
   * 视频生成提示词，可选，最大长度2500字符
   */
  prompt?: string;
  /**
   * 视频时长，必填参数，可选值：'5'或'10'秒
   * @values 5 | 10
   */
  duration: '5' | '10';
  /**
   * 生成模式，标准(std)或专业(pro)，默认std
   * @default "std"
   * @values std | pro
   */
  mode?: 'std' | 'pro';
  /**
   * 视频宽高比，可选，支持16:9、9:16、1:1
   * @values 16:9 | 9:16 | 1:1
   */
  aspect_ratio?: '16:9' | '9:16' | '1:1';
  /**
   * 控制摄像机运动的参数（如果未指定，模型将根据输入文本/图像智能匹配）
   */
  camera_control?: {
    /**
     * 预定义摄像机运动类型 - simple: 基础摄像机运动，在此类型下可在config中选择6个选项之一 - down_back: 摄像机下降并向后移动（向下平移并缩小） - forward_up: 摄像机向前移动并向上倾斜（放大并向上平移） - right_turn_forward: 向右旋转并向前移动 - left_turn_forward: 向左旋转并向前移动
     * @values simple | down_back | forward_up | right_turn_forward | left_turn_forward
     */
    type?: 'simple' | 'down_back' | 'forward_up' | 'right_turn_forward' | 'left_turn_forward';
    /**
     * 包含6个字段，用于指定摄像机在不同方向上的运动或变化 当type设置为simple时为必填，其他类型时应留空 要求6个参数中只能有一个非零，其余必须为零
     */
    config?: Record<string, any>;
  };
  /**
   * 尾帧图像URL或Base64编码，与image二选一（至少提供其中一个）
   */
  image_tail?: string;
  /**
   * 静态遮罩，可选
   */
  static_mask?: string;
  /**
   * 动态遮罩列表，可选
   */
  dynamic_masks?: Record<string, any>[];
  /**
   * 外部任务ID，可选
   */
  external_task_id?: string;
}

export interface KlingV2MasterI2vResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * kling_v2_master_i2v
 *
 * Kling AI V2 Master 图像到视频生成API
 * 
 * Model: kling-v2-master
 * Features: 图像到视频生成，支持标准和专业模式
 * Duration: 5秒或10秒（必填）
 * Aspect Ratio: 16:9、9:16、1:1（可选）
 * Camera Control: 支持多种摄像机运动控制（可选）
 * 
 * API Documentation: https://app.klingai.com/global/dev/document-api/apiReference/model/imageToVideo
 *
 * @example
 * ```typescript
 * import { initSeacloud, klingV2MasterI2v } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await klingV2MasterI2v({
 *   // 参数见 KlingV2MasterI2vParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 KlingV2MasterI2vParams 接口定义
 * @returns 生成的资源列表
 */
export async function klingV2MasterI2v(
  params: KlingV2MasterI2vParams,
): Promise<KlingV2MasterI2vResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'kling_v2_master_i2v',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: KlingV2MasterI2vResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
